import {ErrorInfo, ReactNode} from 'react';
import {ErrorBoundary as ErrorBoundaryLib} from 'react-error-boundary';
import styled from 'styled-components';
import {useSendNcpLog} from '@/api/logger';
import {IconButton} from '@/components/Button';
import {Typography} from '@/components/Typography';
import {LogLevel} from '@/enums';
import {HorizontalFullContainer, PageContent, ScrollBody} from '@/styled-components';
import {MEDIA_QUERIES, palette} from '@/styles';
import Image from './error.svg';
import IconUndo from './icon-undo.svg';

const replaceErrors = (_: any, value: any) => {
  if (value instanceof Error) {
    const error: any = {};

    Object.getOwnPropertyNames(value).forEach((k) => {
      error[k] = (value as any)[k];
    });

    return error;
  }

  return value;
};

const FallbackComponent = () => {
  const reload = () => window.location.reload();

  return (
    <HorizontalFullContainer>
      <Body>
        <Root>
          <Text>
            앗! 오류가 발생했어요.
            <br />
            페이지를 새로고침 한 뒤 서비스를 이용해주세요.
          </Text>
          <ImageWrapper>
            <img src={Image} alt="에러" />
          </ImageWrapper>
          <IconButton
            size="regular"
            variant="outline"
            icon={IconUndo}
            onClick={reload}
          >
            <Typography fontWeight="500" color={palette.black}>
              다시 불러오기
            </Typography>
          </IconButton>
        </Root>
      </Body>
    </HorizontalFullContainer>
  );
};

const ErrorBoundary = (props: {children: ReactNode[] | ReactNode}) => {
  const {mutate} = useSendNcpLog();

  const onError = (error: Error, errorInfo: ErrorInfo) => {
    if (window.location.host.includes('localhost')) {
      return;
    }

    mutate({
      errorInfo,
      logLevel: LogLevel.Error,
      errorType: 'react-error-boundary',
      message: JSON.stringify(error, replaceErrors),
    });
  };

  return (
    <ErrorBoundaryLib
      fallbackRender={FallbackComponent}
      onError={onError}
      {...props}
    />
  );
};

export default ErrorBoundary;

const Body = styled(ScrollBody)`
  display: flex;
`;

const Root = styled(PageContent)`
  align-items: center;
  gap: 8px;
`;

const ImageWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const Text = styled.div`
  color: ${palette.gray600};
  text-align: center;
  font-size: 16px;
  line-height: 1.3;
  ${MEDIA_QUERIES.mobile} {
    font-size: 14px;
  }
`;
